function createClientEventId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
];

class BibleWebRtcClient {
  constructor({ sendSignal, onLocalStream, onRemoteStream, onStateChange, onError }) {
    this.sendSignal = sendSignal;
    this.onLocalStream = onLocalStream;
    this.onRemoteStream = onRemoteStream;
    this.onStateChange = onStateChange;
    this.onError = onError;
    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.pendingOffer = null;
    this.makingOffer = false;
  }

  get hasLocalStream() {
    return !!this.localStream;
  }

  notifyState(state) {
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  notifyError(error) {
    if (this.onError) {
      this.onError(error);
    }
  }

  createPeerConnection() {
    if (this.peerConnection) {
      return this.peerConnection;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.remoteStream = new MediaStream();

    pc.ontrack = (event) => {
      event.streams[0].getTracks().forEach((track) => {
        if (!this.remoteStream.getTracks().some((existing) => existing.id === track.id)) {
          this.remoteStream.addTrack(track);
        }
      });
      if (this.onRemoteStream) {
        this.onRemoteStream(this.remoteStream);
      }
    };

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      this.sendSignal({
        type: 'ICE_CANDIDATE',
        candidate: event.candidate.candidate,
        sdpMid: event.candidate.sdpMid,
        sdpMLineIndex: event.candidate.sdpMLineIndex,
        clientEventId: createClientEventId('ice'),
      });
    };

    pc.onconnectionstatechange = () => {
      this.notifyState(pc.connectionState);
    };

    this.peerConnection = pc;
    return pc;
  }

  attachLocalTracks() {
    const pc = this.createPeerConnection();
    if (!this.localStream) return;

    const senders = pc.getSenders();
    this.localStream.getTracks().forEach((track) => {
      if (!senders.some((sender) => sender.track?.id === track.id)) {
        pc.addTrack(track, this.localStream);
      }
    });
  }

  async getAvailableMediaDevices() {
    if (!navigator.mediaDevices?.enumerateDevices || !navigator.mediaDevices?.getUserMedia) {
      const error = new Error('Recursos de audio e video nao estao disponiveis neste navegador.');
      error.name = 'MediaDevicesUnavailableError';
      throw error;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      hasAudioInput: devices.some((device) => device.kind === 'audioinput'),
      hasVideoInput: devices.some((device) => device.kind === 'videoinput'),
    };
  }

  async captureLocalStream() {
    const { hasAudioInput, hasVideoInput } = await this.getAvailableMediaDevices();

    if (!hasAudioInput) {
      const error = new Error('Nenhum microfone foi encontrado. Verifique os dispositivos conectados.');
      error.name = 'NoAudioInputError';
      throw error;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: hasVideoInput,
      });

      return {
        stream,
        audioOnly: !hasVideoInput || stream.getVideoTracks().length === 0,
        message: hasVideoInput ? '' : 'Camera nao encontrada. A chamada foi iniciada apenas com audio.',
      };
    } catch (error) {
      if (hasVideoInput && error?.name === 'NotFoundError') {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        return {
          stream,
          audioOnly: true,
          message: 'Camera nao encontrada. A chamada foi iniciada apenas com audio.',
        };
      }

      throw error;
    }
  }

  async start() {
    try {
      let mediaStatus = null;

      if (!this.localStream) {
        mediaStatus = await this.captureLocalStream();
        this.localStream = mediaStatus.stream;
        if (this.onLocalStream) {
          this.onLocalStream(this.localStream);
        }
      } else {
        mediaStatus = {
          stream: this.localStream,
          audioOnly: this.localStream.getVideoTracks().length === 0,
          message: '',
        };
      }

      this.attachLocalTracks();
      this.sendSignal({ type: 'JOIN', clientEventId: createClientEventId('call-join') });

      if (this.pendingOffer) {
        const offer = this.pendingOffer;
        this.pendingOffer = null;
        await this.answerOffer(offer);
      } else {
        await this.createAndSendOffer();
      }

      this.notifyState('local-ready');
      return mediaStatus;
    } catch (error) {
      this.notifyError(error);
      throw error;
    }
  }

  async createAndSendOffer() {
    const pc = this.createPeerConnection();
    if (!this.localStream || pc.signalingState !== 'stable') return;

    try {
      this.makingOffer = true;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.sendSignal({
        type: 'OFFER',
        sdp: offer.sdp,
        clientEventId: createClientEventId('offer'),
      });
    } finally {
      this.makingOffer = false;
    }
  }

  async answerOffer(signal) {
    const pc = this.createPeerConnection();
    this.attachLocalTracks();

    await pc.setRemoteDescription({ type: 'offer', sdp: signal.sdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    this.sendSignal({
      type: 'ANSWER',
      sdp: answer.sdp,
      clientEventId: createClientEventId('answer'),
    });
  }

  async handleSignal(signal) {
    if (!signal?.type) return;

    try {
      if (signal.type === 'JOIN') {
        if (this.localStream) {
          await this.createAndSendOffer();
        } else {
          this.notifyState('remote-waiting');
        }
        return;
      }

      if (signal.type === 'LEAVE') {
        this.clearRemoteStream();
        this.notifyState('remote-left');
        return;
      }

      if (signal.type === 'OFFER') {
        if (!this.localStream) {
          this.pendingOffer = signal;
          this.notifyState('incoming-offer');
          return;
        }

        const pc = this.createPeerConnection();
        const offerCollision = this.makingOffer || pc.signalingState !== 'stable';
        if (offerCollision && pc.signalingState !== 'stable') {
          await pc.setLocalDescription({ type: 'rollback' });
        }

        await this.answerOffer(signal);
        return;
      }

      if (signal.type === 'ANSWER') {
        const pc = this.createPeerConnection();
        if (pc.signalingState !== 'stable') {
          await pc.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
        }
        return;
      }

      if (signal.type === 'ICE_CANDIDATE' && signal.candidate) {
        const pc = this.createPeerConnection();
        await pc.addIceCandidate(new RTCIceCandidate({
          candidate: signal.candidate,
          sdpMid: signal.sdpMid,
          sdpMLineIndex: signal.sdpMLineIndex,
        }));
      }
    } catch (error) {
      this.notifyError(error);
    }
  }

  setAudioEnabled(enabled) {
    this.localStream?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  setVideoEnabled(enabled) {
    this.localStream?.getVideoTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  clearRemoteStream() {
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach((track) => track.stop());
    }
    this.remoteStream = null;
    if (this.onRemoteStream) {
      this.onRemoteStream(null);
    }
  }

  end() {
    this.sendSignal({ type: 'LEAVE', clientEventId: createClientEventId('call-leave') });

    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;

    this.peerConnection?.close();
    this.peerConnection = null;
    this.pendingOffer = null;
    this.makingOffer = false;
    this.clearRemoteStream();

    if (this.onLocalStream) {
      this.onLocalStream(null);
    }
    this.notifyState('ended');
  }
}

export default BibleWebRtcClient;
